import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { TasksCategory } from '../models/tasks-category';
import { MainTasksService } from '../main-tasks.service';
import { TasksModule } from '../models/tasks-module';
import { CommomAnalyticsIndicator } from '../models/commom-analytics-indicator';

@Component({
  selector: 'main-tasks',
  templateUrl: './main-tasks.component.html',
  styleUrls: ['./main-tasks.component.scss'],
})
export class MainTasksComponent implements OnInit {
  allTasks: any = [];
  categories: any = [];
  allTasksCount = {};
  @Output() onTotalChanged = new EventEmitter<number>();
  @Output() onLoadingChanged = new EventEmitter<number>();
  @Output() onError = new EventEmitter<{ allHadErrors: boolean; hadAtLeastOneError: boolean }>();
  @Output() onActionPlanClick = new EventEmitter<CommomAnalyticsIndicator>();
  @Output() onDefectTagClick = new EventEmitter<CommomAnalyticsIndicator>();
  @Output() onCilClick = new EventEmitter<CommomAnalyticsIndicator>();

  public apiErrors = {
    'Action Plan': false,
    Checklist: false,
    CIL: false,
    'Defect Tag': false,
  };
  public allHadErrors = false;
  public hadAtLeastOneError = false;
  public isTotalTasksLoading = true;
  public isLoadingTasks = false;

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _totalTasks: any = {};
  #isFirstTime = true;
  #typeToUpdate = TasksCategory.TODAY;

  constructor(
    private _mainTasksService: MainTasksService,
    private _router: Router
  ) {}

  ngOnInit(): void {
    this.#setCategories();
    this.allTasks = this._mainTasksService._allTasks;
    this.updateTotalTasks();
    this.#tasksUpdate();
    this.#tasksLoading();
    this.#tasksError();
    this.#bookmarkUpdate();
  }

  // A favorite toggled outside the bar (e.g. the Action Plan or Defect Tag revamp
  // modal) is relayed here so the matching row's star repaints in place — the
  // rendered tasks are clones, so the service cannot mutate them directly (AB#68784).
  #bookmarkUpdate(): void {
    this._mainTasksService.bookmarkUpdated$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ moduleName, uniqueId, isBookmark }) => {
      const target = Number(uniqueId);
      if (!Number.isFinite(target)) return;
      Object.values(this.allTasks ?? {}).forEach((tasks) => {
        if (!Array.isArray(tasks)) return;
        tasks.forEach((task: CommomAnalyticsIndicator) => {
          if (task?.moduleName === moduleName && Number(task.uniqueId) === target) {
            task.isBookmark = isBookmark;
          }
        });
      });
    });
  }

  #setCategories(): void {
    this.categories = [
      {
        enum: [TasksCategory.LATE],
        titleKey: 'Atrasados',
        opened: false,
      },
      {
        enum: [TasksCategory.TODAY],
        titleKey: 'Hoje',
        opened: true,
      },
      {
        enum: [TasksCategory.TOMORROW_UP_TO_7_DAYS],
        titleKey: '<= 7 dias',
        opened: false,
      },
      {
        enum: [TasksCategory.MORE_THAN_7_DAYS],
        titleKey: '> 7 dias',
        opened: false,
      },
    ];
  }

  #tasksUpdate(): void {
    this._mainTasksService.allTasksUpdated$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
      const filterModule = this._mainTasksService.getFilterModule();
      if (this.isLoadingTasks) {
        if (filterModule != null) {
          this.allTasks[this.#typeToUpdate] = value[this.#typeToUpdate].filter((task: any) => task?.moduleName === filterModule);
        } else this.allTasks[this.#typeToUpdate] = value[this.#typeToUpdate];
      } else {
        if (filterModule != null) {
          this.categories?.forEach((categorie) => {
            const values = value[categorie?.enum[0]]?.filter((task: any) => task?.moduleName === filterModule);
            this.allTasks[categorie?.enum[0]] = values;
          });
        } else this.allTasks = value;

        this.updateTotalTasks();
      }
    });
  }

  #tasksLoading(): void {
    this._mainTasksService.isAllTasksLoading$.pipe(takeUntil(this._unsubscribeAll)).subscribe((isLoading) => {
      if (this.#isFirstTime || !this.isLoadingTasks) {
        this.isTotalTasksLoading = isLoading;
        this.onLoadingChanged.emit(isLoading);
        this.categories?.forEach((categorie) => {
          const type = categorie?.enum[0] === this.#typeToUpdate ? this.#typeToUpdate : categorie?.enum?.[0];
          if (categorie?.enum?.[0] === this.#typeToUpdate || categorie.opened) categorie.opened = this.allTasks[type]?.length > 0;
        });
      }

      if (!isLoading) {
        this.allHadErrors = Object.keys(this.apiErrors).every((key) => this.apiErrors[key]);
        this.hadAtLeastOneError = Object.keys(this.apiErrors).filter((key) => this.apiErrors[key]).length > 0;
        this.onError.emit({ allHadErrors: this.allHadErrors, hadAtLeastOneError: this.hadAtLeastOneError });
        this.#setCount();
        this.isLoadingTasks = false;
      } else {
        for (const key in this.apiErrors) {
          this.apiErrors[key] = false;
        }
      }
    });
  }

  #tasksError(): void {
    this._mainTasksService.hasError$.pipe(takeUntil(this._unsubscribeAll)).subscribe((errorInfo) => {
      this.apiErrors[errorInfo.apiName] = true;
    });
  }

  #setCount(): void {
    if (this.#isFirstTime || !this.isLoadingTasks) {
      this.#isFirstTime = false;
      this.allTasksCount = this._mainTasksService._allTasks?.count;
      this.updateTotalTasks();
    } else {
      let isUpdate: boolean;
      Object.keys(this.allTasksCount).forEach((key) => {
        if (this.allTasksCount?.[key] !== this._mainTasksService._allTasks?.count?.[key]) {
          isUpdate = true;
          this.allTasksCount[key] = this._mainTasksService._allTasks?.count?.[key];
        }
      });
      if (isUpdate) this.updateTotalTasks();
    }
  }

  public getTasks(): void {
    this._mainTasksService.getUserDrillDowns();
    this.allHadErrors = Object.keys(this.apiErrors).every((key) => this.apiErrors[key]);
  }

  private updateTotalTasks(): void {
    this._totalTasks = Object.values(this.allTasksCount).reduce((total: number, tasks: number) => total + tasks, 0);
    this.onTotalChanged.emit(this._totalTasks);
  }

  public openCategory(category: TasksCategory): void {
    if (this._mainTasksService.categoryNeedsLoad(category, this.allTasks)) {
      this.isLoadingTasks = true;
      this.#typeToUpdate = category;
      this.allTasks = { ...this.allTasks };

      const filterModule = this._mainTasksService.getFilterModule();
      const updateOnly =
        filterModule != null && filterModule != undefined
          ? {
              actionPlan: filterModule == TasksModule.ACTIONPLAN,
              checklist: filterModule == TasksModule.CHECKLIST,
              lil: filterModule == TasksModule.CIL,
              defectTag: filterModule == TasksModule.DEFECTTAG,
              kaizen: false,
            }
          : null;

      if (this._router.url.includes('dashboard/subordinates')) {
        this._mainTasksService.getSubordinatesDrillDowns(updateOnly, category);
      } else {
        this._mainTasksService.getUserDrillDowns(updateOnly, category);
      }
    }
  }
}
